import React, { useCallback, useMemo, useState } from "react";

// packages
import { useTranslation } from "react-i18next";

// components
import { MilestoneStatus } from "../milestones/status";

// helpers
import { formatDateToArabic } from "../../../setup/util/helpers";

// styles
import styles from "./milestone.module.scss";

// hooks
import useLocalStorage from "../../../hooks/useLocalStorage/useLocalStorage";

// providers and context

// types
import {
  EContractType,
  ESenderRole,
  IContractDetailsResponseData,
  UserProfile,
} from "../../../setup/api/services/contract/models";
import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../enums";
import PayeeTable from "../payeeTable";
import ReceivingTable from "../receivingTable";
import { IUser } from "../../../setup/api/services/auth/models";
import ArbitratorTable from "../arbitratorTable";
import BrokerTable from "../brokerTable";
import classNames from "classnames";
import { Badge, IconButton, Menu, MenuItem } from "@mui/material";
import MoreIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import {
  ACTION_SEND_MONEY_TO_PAYEES,
  ACTION_SEND_MONEY_TO_PROVIDERS,
} from "../../../pages/contract/actions/constants";
import { externalDoAction } from "../../../setup/api/services/contractActions";
import { getAccessTokenSync } from "../../../setup/auth/oidc";
import { EContractActions } from "../../../setup/api/services/contractActions/enum";

interface Props {
  milestone: IContractDetailsResponseData;
  index: number;
  milestoneLength: number;
  isPreviousMilestonePaid: (milestoneIndex: number) => boolean;
  fetchProviders: (
    contract: IContractDetailsResponseData
  ) => Partial<UserProfile & { userId?: string }>[];
  getActorType: (contract: IContractDetailsResponseData) => ESenderRole;
}

const Milestone = ({
  milestone,
  index,
  milestoneLength,
  fetchProviders,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const userToken = getAccessTokenSync();
  const [userInfo] = useLocalStorage<IUser | null>("userInfo", null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const partiesMap = useMemo(() => {
    return milestone?.parties?.reduce((acc, item) => {
      if (item?.phoneNumber) acc[item.phoneNumber || ""] = item;
      return acc;
    }, {});
  }, [milestone?.parties]);

  const isActionsAllowed = useMemo(() => {
    return (
      milestone?.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY ||
      milestone?.status === ECONTRACT_STATUS.PARTIAL_CONFIRMED_DELIVERY
    );
  }, [milestone?.status]);

  const isClientAdmin = useMemo(() => {
    if (!userToken) return false;
    const base64Url = userToken.split(".")[1]; // Get the payload part of the token
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const data = JSON.parse(jsonPayload);
    return data?.authorities?.some((role) => role === "CLIENT_ADMIN");
  }, [userToken]);

  const handleActionsMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setActionsAnchorEl(event.currentTarget);
    },
    []
  );

  const handleSendMoneyToProviders = useCallback(async () => {
    const body = {
      contractId: milestone?.id,
      userId: userInfo?.id || "",
      actorRole: isClientAdmin ? ESenderRole.CLIENT_ADMIN : ESenderRole.ADMIN,
      contractAction: EContractActions.ACCEPT_CONTRACT,
      contractType: EContractType.MILESTONE_CONTRACT,
    };
    await externalDoAction(body);
    window.location.reload();
  }, [milestone?.id, isClientAdmin, userInfo?.id]);

  const handleSendMoneyToPayees = useCallback(async () => {
    const body = {
      contractId: milestone?.id,
      userId: userInfo?.id || "",
      actorRole: isClientAdmin ? ESenderRole.CLIENT_ADMIN : ESenderRole.ADMIN,
      contractAction: EContractActions.REJECT_CONTRACT,
      contractType: EContractType.MILESTONE_CONTRACT,
    };
    await externalDoAction(body);
    window.location.reload();
  }, [milestone?.id, isClientAdmin, userInfo?.id]);

  const canMakeContractActions = useCallback(
    (userInfo) => {
      if (!userToken) return false;
      const base64Url = userToken.split(".")[1]; // Get the payload part of the token
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      const data = JSON.parse(jsonPayload);
      const isClientAdmin = data?.authorities?.some(
        (role) => role === "CLIENT_ADMIN"
      );
      if (isClientAdmin) return false;
      return (
        (milestone?.status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY ||
          milestone?.status === ECONTRACT_STATUS.PARTIALLY_PAID ||
          milestone?.status === ECONTRACT_STATUS.PARTIALLY_RELEASED ||
          milestone?.status === ECONTRACT_STATUS.PARTIAL_CONFIRMED_DELIVERY ||
          milestone?.status === ECONTRACT_STATUS.PARTIALLY_RESOLVE_RELEASED ||
          milestone?.status === ECONTRACT_STATUS.PARTIALLY_REFUNDED ||
          milestone?.status === ECONTRACT_STATUS.DELIVERY_ESCALATION ||
          milestone?.status === ECONTRACT_STATUS.RESOLVED_RELEASE_PROVIDER ||
          milestone?.status === ECONTRACT_STATUS.RESOLVED_REFUND_CUSTOMER ||
          milestone?.status === ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION ||
          milestone?.status === ECONTRACT_STATUS.ITEM_ACCEPTED) &&
        (milestone?.status === ECONTRACT_STATUS.PARTIALLY_PAID ||
        milestone?.status === ECONTRACT_STATUS.PENDING_PAYMENT_VERIFICATION
          ? userInfo?.phoneNumber
            ? partiesMap?.[userInfo?.phoneNumber || ""]?.status ===
              EPARTY_STATUS.PAYMENT_PENDING_APPROVAL
            : milestone?.parties?.find(
                (party) =>
                  party?.userId === String(userInfo?.userIdentification)
              )?.status === EPARTY_STATUS.PAYMENT_PENDING_APPROVAL
          : partiesMap?.[userInfo?.phoneNumber || ""]?.status !==
              EPARTY_STATUS.WAITING_FOR_ITEM_ACCEPTANCE &&
            partiesMap?.[userInfo?.phoneNumber || ""]?.status !==
              EPARTY_STATUS.WAITING_FOR_PAYMENT &&
            partiesMap?.[userInfo?.phoneNumber || ""]?.status !==
              EPARTY_STATUS.RELEASED &&
            partiesMap?.[userInfo?.phoneNumber || ""]?.status !==
              EPARTY_STATUS.REFUNDED)
      );
    },
    [milestone?.parties, milestone?.status, partiesMap, userToken]
  );

  return (
    <div
      style={{
        width: "100%",
        marginBottom: index !== milestoneLength - 1 ? "48px" : "",
      }}
      className={styles["vertical_container"]}>
      <div className={styles["milestone_title"]}>
        <p className={styles["timeline_dot"]}>{index + 1}</p>
        <p className="subtitle1">
          {t("INSTALLMENT_NUMBER", { val: index + 1 })}
        </p>
        {isActionsAllowed && (
          <IconButton
            size="large"
            aria-label="show more"
            aria-controls={"ContractActions"}
            aria-haspopup="true"
            onClick={handleActionsMenuOpen}
            color="inherit"
            style={{ marginRight: "auto", marginBlock: "auto" }}>
            <MoreIcon />
          </IconButton>
        )}
        <Menu
          anchorEl={actionsAnchorEl}
          dir="rtl"
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          id={milestone?.id}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          open={!!actionsAnchorEl}
          onClose={() => setActionsAnchorEl(null)}>
          <MenuItem onClick={handleSendMoneyToProviders}>
            <IconButton size="large" color="inherit">
              <Badge>
                <EditIcon />
              </Badge>
            </IconButton>
            <p>{ACTION_SEND_MONEY_TO_PROVIDERS}</p>
          </MenuItem>
          <MenuItem onClick={handleSendMoneyToPayees}>
            <IconButton size="large" color="inherit">
              <Badge>
                <EditIcon />
              </Badge>
            </IconButton>
            <p>{ACTION_SEND_MONEY_TO_PAYEES}</p>
          </MenuItem>
        </Menu>
      </div>
      <div style={{ display: "flex", gap: "35px" }}>
        <hr
          style={{
            marginRight: "24px",
            width: "0.5px",
            visibility:
              index === milestoneLength - 1 && milestoneLength > 1
                ? "hidden"
                : "visible",
          }}
        />
        <div className={styles["vertical_container"]} style={{ flex: 1 }}>
          <div className={styles["info_container"]} style={{ gap: "30px" }}>
            <h4 style={{ margin: 0 }}>
              {milestone?.itemPrice} <span> {t("lbl_saudi_currency")}</span>
            </h4>
            <div className={styles["info_container"]}>
              <p style={{ color: "#404D58" }}>
                {t("DATE_ENDS", {
                  val: formatDateToArabic(milestone?.deadLine?.toString()),
                })}
              </p>
            </div>
          </div>
          <p style={{ margin: "0 0 16px 0" }}>{milestone?.itemDetail?.title}</p>
          <div className={styles["milestone_flex_container"]}>
            <MilestoneStatus
              contract={milestone}
              fetchProviders={fetchProviders}
              userId={userInfo?.id}
            />
          </div>

          {partiesMap &&
            milestone?.parties?.find(
              (broker) =>
                broker?.role === ESenderRole.BROKER && !!broker?.arbitrator
            ) && (
              <div className={styles["parties_container"]}>
                <p className={styles["subtitle4"]}>
                  {t("lbl_arbitrator_title")}
                </p>
                <div
                  className={classNames(
                    styles["parties_table"],
                    styles["arbitrating_parties"]
                  )}>
                  <ArbitratorTable
                    milestone={milestone}
                    canMakeContractActions={canMakeContractActions}
                    partiesObj={partiesMap}
                  />
                </div>
              </div>
            )}

          {partiesMap && milestone?.partiesProfiles?.CUSTOMER?.length && (
            <div className={styles["parties_container"]}>
              <p className={styles["subtitle4"]}>{t("lbl_buyers")}</p>
              <div
                className={classNames(
                  styles["parties_table"],
                  styles["paying_parties"]
                )}>
                <PayeeTable
                  milestone={milestone}
                  canMakeContractActions={canMakeContractActions}
                  partiesObj={partiesMap}
                />
              </div>
            </div>
          )}

          {partiesMap && milestone?.partiesProfiles?.PROVIDER?.length && (
            <div className={styles["parties_container"]}>
              <p className={styles["subtitle4"]}>{t("lbl_beneficiaries")}</p>
              <div
                className={classNames(
                  styles["parties_table"],
                  styles["receiving_parties"]
                )}>
                <ReceivingTable
                  milestone={milestone}
                  partiesObj={partiesMap}
                  canMakeContractActions={canMakeContractActions}
                />
              </div>
            </div>
          )}

          {partiesMap &&
            milestone?.partiesProfiles?.BROKER?.length &&
            milestone?.parties?.some(
              (party) =>
                !party?.arbitrator && party?.role === ESenderRole.BROKER
            ) && (
              <div className={styles["parties_container"]}>
                <p className={styles["subtitle4"]}>{t("lbl_broker_title")}</p>
                <div
                  className={classNames(
                    styles["parties_table"],
                    styles["broking_parties"]
                  )}>
                  <BrokerTable
                    canMakeContractActions={canMakeContractActions}
                    milestone={milestone}
                    partiesObj={partiesMap}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default Milestone;
